import { useCallback, useMemo } from 'react'
import useLocalStorage from './useLocalStorage'
import { UserDetails } from '../components/Routes/AuthProvider'

/**
 * Represents the loop state of a playlist for a specific user and actor.
 */
export interface PlaylistLoopInfo {
  userKey: number
  actorKey: number | 'parent'
  isLoop: boolean
}

/**
 * Hook to manage the playlist "loop" state for the current user.
 *
 * This hook uses `localStorage` to persist the loop state associated with
 * the current user's `userKey` and `actorKey`.
 *
 * ### What it does:
 * - Returns `isLoopEnabled`: a boolean indicating whether the playlist is currently looping for the user.
 * - Returns `toggleLoop()`: a function that toggles the loop state:
 *    - If no configuration exists yet, it creates one with `isLoop: true`.
 *    - If a configuration already exists, it removes it (equivalent to disabling loop).
 *
 * ### Example usage:
 * ```tsx
 * const { isLoopEnabled, toggleLoop } = usePlaylistLoop();
 *
 * return (
 *   <button onClick={toggleLoop}>
 *     {isLoopEnabled ? 'Disable Loop' : 'Enable Loop'}
 *   </button>
 * );
 * ```
 *
 * @returns {{
 *   isLoopEnabled: boolean;
 *   toggleLoop: () => void;
 * }}
 */
export const usePlaylistLoop = (): {
  isLoopEnabled: boolean
  toggleLoop: () => void
} => {
  const [playlistLoopInfo, setPlaylistLoopInfo] = useLocalStorage<
    Array<PlaylistLoopInfo>
  >('playlistLoopInfo', [])

  const [userDetails] = useLocalStorage<Required<UserDetails>>('userDetails', {
    actingAs: 'parent',
    userKey: 0,
  })

  /**
   * Determines whether the playlist is currently in loop mode
   * for the current user and actor.
   */
  const isLoopEnabled = useMemo(() => {
    return playlistLoopInfo?.some(
      (loopInfo) =>
        loopInfo.actorKey === userDetails.actingAs &&
        loopInfo.userKey === userDetails.userKey
    )
  }, [playlistLoopInfo, userDetails])

  /**
   * Toggles the loop state:
   * - If no record exists yet, adds one with `isLoop: true`.
   * - If it already exists, removes it from the state.
   */
  const toggleLoop = useCallback(() => {
    const userLoopConfig = {
      userKey: userDetails.userKey,
      actorKey: userDetails.actingAs,
      isLoop: true,
    }

    /**
     * If there is no playlistLoopInfo yet, initialize it with a new entry
     * for the current user with loop enabled
     */
    if (!playlistLoopInfo) {
      setPlaylistLoopInfo([userLoopConfig])
      return
    }
    /** Find the index of the current user's loop configuration (if it exists) */
    const index = playlistLoopInfo.findIndex(
      (loopInfo) =>
        loopInfo.actorKey === userDetails.actingAs &&
        loopInfo.userKey === userDetails.userKey
    )

    if (index === -1) {
      /** If no configuration exists for the current user, add a new entry with loop enabled */
      setPlaylistLoopInfo([...playlistLoopInfo, userLoopConfig])
    } else {
      // If configuration already exists, remove the current user's loop entry to disable loop

      setPlaylistLoopInfo(
        playlistLoopInfo.filter((_, position) => position !== index)
      )
    }
  }, [playlistLoopInfo, setPlaylistLoopInfo, userDetails])

  return { isLoopEnabled, toggleLoop }
}
